import React, { useState, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { Locale } from '@/i18n/config';
import { useCandidateQuestionsAnswers } from '../../hooks/useJobOffers';
import { formatQuestionKind, formatQuestionTimerDisplay, formatQuestionTimerTitle } from '../../utils/formatUtils';
import { getQuestionTimeSpentIntent } from '../../utils/tag.utils';
import { Tag } from '../ui/atoms/Tag';
import { IconWrapper } from '../ui/atoms';
import { MonacoIDE } from '../interview/MonacoIDE';
import { QuestionCheatingDetection, VideoTime, SuspectedSegment, CandidateAssessmentSummary } from '../../types/job.types';
import { SuspiciousActivityBadge } from '../ui/SuspiciousActivityBadge';
import { useAuth0 } from '@auth0/auth0-react';
import { ChevronDown, ChevronRight, CodeXml, CircleHelp, TimerIcon, Users, ShieldCheck } from 'lucide-react';
import { resolveInferredExplanation } from '../../utils/assessment/inferred-explanation';
import { getFixedTForContentLocale } from '@/utils/locale';
import { Button } from '@/components/ui/atoms/Button';
import { clearCandidateCheatingFlag } from '../../services/job.service';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import { toast } from 'sonner';
import { getQuestionVideoBlob, getQuestionVideoStreamUrl } from '../../services/interview.service';
import { useStreamableVideo } from '../../hooks/useStreamableVideo';
import { RecordedVideo } from '../ui/molecules/RecordedVideo';


// Helper function to get question type icon
const getQuestionTypeIcon = (type: string) => {
  if (type === 'technical' || type === 'code-read-only' || type === 'code-write') {
    return (
      <IconWrapper size="sm" color="purple">
        <CodeXml />
      </IconWrapper>
    );
  } else {
    return (
      <IconWrapper size="sm" className="text-dark-invirtus/60">
        <Users />
      </IconWrapper>
    );
  }
};

// Helper function to toggle question expansion
const toggleQuestionExpansion = (questionIndex: number, setExpandedQuestions: React.Dispatch<React.SetStateAction<Record<number, boolean>>>) => {
  setExpandedQuestions(prev => ({
    ...prev,
    [questionIndex]: !prev[questionIndex]
  }));
};

const extractCodeFromQuestion = (text: string) => {
  const codeBlockRegex = /```([\s\S]*?)```/g;
  const match = codeBlockRegex.exec(text);
  return { code: match ? match[1].trim() : '' };
};

const removeCodeBlocksFromQuestion = (text: string) =>
  text.replace(/```[\s\S]*?```/g, '').trim();

// Cheating detection helpers
const formatTime = (t: VideoTime) =>
  `${String(t.minute).padStart(2, '0')}:${String(t.second).padStart(2, '0')}`;

const timeToSeconds = (t: VideoTime) => t.minute * 60 + t.second;

type LevelConfig = Record<
  'low' | 'medium' | 'high',
  { label: string; color: string; intent: 'warning' | 'error' }
>;

const getLevelConfig = (t: TFunction<'candidates'>): LevelConfig => ({
  low: {
    label: t('meta_cv.qa.suspicion_low'),
    color: 'bg-amber-400/70',
    intent: 'warning',
  },
  medium: {
    label: t('meta_cv.qa.suspicion_medium'),
    color: 'bg-orange-500/70',
    intent: 'warning',
  },
  high: {
    label: t('meta_cv.qa.suspicion_high'),
    color: 'bg-red-500/70',
    intent: 'error',
  },
});

export const CHEATING_THRESHOLD = 80;

interface CandidateQuestionsAnswersProps {
  candidateId: string;
  hasAssessment: boolean;
  cheatingDetection?: Record<string, QuestionCheatingDetection>;
  perQuestionScores?: Array<{
    question_index: number;
    score: number;
    anchor_level: string;
    evidence: string;
    justification: string;
    skill_area: string;
    is_inferred?: boolean;
    score_explanation?: string;
  }>;
  assessmentContentLocale?: Locale;
  /** Called with the refreshed assessment summary after a reviewer clears a
   *  question's suspicious-activity flag, so the parent can propagate the
   *  removal to every badge surface. */
  onCheatingFlagCleared?: (summary: CandidateAssessmentSummary) => void;
}

// QuestionVideoPlayer component for cheating analysis
interface QuestionVideoPlayerProps {
  interviewId: string;
  questionIndex: number;
  segments: SuspectedSegment[];
}

export interface QuestionVideoPlayerHandle {
  jumpToSegment: (segment: SuspectedSegment) => void;
}

const QuestionVideoPlayer = forwardRef<QuestionVideoPlayerHandle, QuestionVideoPlayerProps>(({ 
  interviewId, 
  questionIndex,
  segments 
}, ref) => {
  const { t } = useTranslation('candidates');
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [activeSegment, setActiveSegment] = useState(-1);
  const { getAccessTokenSilently } = useAuth0();

  // Streamed from the bucket in staging/production, downloaded through the API
  // otherwise (see hooks/useStreamableVideo).
  const video = useStreamableVideo(`${interviewId}:${questionIndex}`, {
    resolveSource: async () => {
      const token = await getAccessTokenSilently();
      const response = await getQuestionVideoStreamUrl(token, interviewId, questionIndex);
      // Any failure here (no video, older backend) falls through to the API
      // download, which reports the video as unavailable if it truly is.
      return { streamUrl: response.data?.stream_url ?? null };
    },
    fetchBlob: async () => {
      const token = await getAccessTokenSilently();
      const response = await getQuestionVideoBlob(token, interviewId, questionIndex);
      if (response.error || !response.data) {
        throw new Error(response.error ?? 'Video not available');
      }
      return response.data;
    },
  });
  const jumpToSegment = (segment: SuspectedSegment) => {
    if (videoRef.current) {
      videoRef.current.currentTime = timeToSeconds(segment.start_time);
      videoRef.current.play()?.catch(() => undefined);
    }
  };

  // Expose jumpToSegment to parent via ref
  useImperativeHandle(ref, () => ({
    jumpToSegment
  }));

  const handleTimeUpdate = (event: React.SyntheticEvent<HTMLVideoElement>) => {
    const currentTime = event.currentTarget.currentTime;
    let found = -1;
    segments.forEach((segment, index) => {
      const start = timeToSeconds(segment.start_time);
      const end = timeToSeconds(segment.end_time);
      if (currentTime >= start && currentTime <= end) {
        found = index;
      }
    });
    setActiveSegment(found);
  };

  return (
    <div className="space-y-2">
      <RecordedVideo
        video={video}
        videoRef={videoRef}
        loadingLabel={t('meta_cv.qa.video_loading')}
        unavailableLabel={t('meta_cv.qa.video_unavailable')}
        className="w-full aspect-video max-h-64"
        onLoadedMetadata={(e) => setVideoDuration(e.currentTarget.duration)}
        onTimeUpdate={handleTimeUpdate}
      />

      {/* Mini Timeline */}
      {videoDuration > 0 && segments.length > 0 && (
        <div className="h-3 bg-gray-200 rounded-full relative overflow-hidden">
          {segments.map((segment, index) => {
            const startPct = (timeToSeconds(segment.start_time) / videoDuration) * 100;
            const widthPct = ((timeToSeconds(segment.end_time) - timeToSeconds(segment.start_time)) / videoDuration) * 100;
            return (
              <button
                key={index}
                onClick={() => jumpToSegment(segment)}
                className={`absolute h-full cursor-pointer transition-all hover:brightness-110 ${getLevelConfig(t)[segment.suspection_level].color} ${
                  activeSegment === index ? 'ring-2 ring-gray-800' : ''
                }`}
                style={{ left: `${startPct}%`, width: `${Math.max(widthPct, 2)}%` }}
                title={`${formatTime(segment.start_time)} - ${formatTime(segment.end_time)}`}
              />
            );
          })}
        </div>
      )}
    </div>
  );
});

export const CandidateQuestionsAnswers: React.FC<CandidateQuestionsAnswersProps> = ({
  candidateId,
  hasAssessment,
  cheatingDetection,
  perQuestionScores,
  assessmentContentLocale = 'en',
  onCheatingFlagCleared,
}) => {
  const { t } = useTranslation('candidates');
  const { getAccessTokenSilently } = useAuth0();
  const [confirmClearIndex, setConfirmClearIndex] = useState<number | null>(null);
  const [clearingIndex, setClearingIndex] = useState<number | null>(null);

  const handleClearCheatingFlag = async (questionIndex: number) => {
    setClearingIndex(questionIndex);
    try {
      const token = await getAccessTokenSilently();
      const response = await clearCandidateCheatingFlag(token, candidateId, questionIndex);
      if (response.error || !response.data) {
        throw new Error(response.error || 'clear_failed');
      }
      onCheatingFlagCleared?.(response.data);
      toast.success(t('meta_cv.qa.clear_flag_success'));
      setConfirmClearIndex(null);
    } catch (error) {
      console.error('Error clearing cheating flag:', error);
      toast.error(t('meta_cv.qa.clear_flag_error'));
    } finally {
      setClearingIndex(null);
    }
  };
  const tInferred = useMemo<TFunction<'candidates'>>(
    () => getFixedTForContentLocale(assessmentContentLocale),
    [assessmentContentLocale],
  );
  const levelConfig = useMemo(
    () => getLevelConfig(tInferred),
    [tInferred],
  );
  const [expandedQuestions, setExpandedQuestions] = useState<Record<number, boolean>>({});
  const [expandedCheatingPanels, setExpandedCheatingPanels] = useState<Record<number, boolean>>({});
  const [expandedSegments, setExpandedSegments] = useState<Record<string, boolean>>({});
  const [isExpanded, setIsExpanded] = useState(false); // Default collapsed
  const videoPlayerRefs = useRef<Record<number, QuestionVideoPlayerHandle | null>>({});
  
  const toggleSegmentExpansion = (questionIndex: number, segmentIndex: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const key = `${questionIndex}-${segmentIndex}`;
    setExpandedSegments(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  
  const toggleCheatingPanel = (index: number) => {
    setExpandedCheatingPanels(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  
  const handleSegmentClick = (questionIndex: number, segment: SuspectedSegment) => {
    const playerRef = videoPlayerRefs.current[questionIndex];
    if (playerRef) {
      playerRef.jumpToSegment(segment);
    }
  };
  
  // Fetch candidate questions and answers
  const { 
    data: questionsAnswersData, 
    isLoading: isLoadingQuestionsAnswers, 
    error: questionsAnswersError 
  } = useCandidateQuestionsAnswers(candidateId, { enabled: hasAssessment });

  if (!hasAssessment) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-light-lila/60 bg-white shadow-sm">
        {/* Collapsible Header */}
        <div 
          className="flex items-start justify-between gap-4 cursor-pointer rounded-xl px-4 py-4 sm:px-6 sm:py-5 transition-colors hover:bg-light-lila/10"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-start space-x-3">
            <div className="mt-0.5">
              <IconWrapper size="md" color="purple">
                <CircleHelp />
              </IconWrapper>
            </div>
            <div>
              <h5 className="mb-1 text-dark-invirtus">{t('meta_cv.qa.title')}</h5>
              <p className="font-body text-sm text-dark-invirtus/70">
                {t('meta_cv.qa.description')}
              </p>
            </div>
          </div>
          {/* Expand/Collapse Icon */}
          <div className="flex-shrink-0">
            <IconWrapper
              size="md"
              className={`text-dark-invirtus/60 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
            >
              <ChevronDown />
            </IconWrapper>
          </div>
        </div>

        {/* Collapsible Content */}
        {isExpanded && (
          <>
            {isLoadingQuestionsAnswers ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple"></div>
                <span className="ml-2 text-dark-invirtus/70 font-body text-sm">{t('meta_cv.qa.loading')}</span>
              </div>
            ) : questionsAnswersError ? (
              <div className="text-center py-8">
                <p className="text-red-600 font-body text-sm">{t('meta_cv.qa.error', { message: questionsAnswersError.message })}</p>
              </div>
            ) : !questionsAnswersData?.questions_answers?.length ? (
              <div className="text-center py-8">
                <p className="text-dark-invirtus/70 font-body text-sm">{t('meta_cv.qa.empty')}</p>
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4 m-3 sm:m-6">
                {questionsAnswersData.questions_answers.map((qa, index) => {
                  const isQuestionExpanded = expandedQuestions[index] ?? false;
                  const { code: questionCode } = extractCodeFromQuestion(qa.question_text);
                  const codeToShow = questionCode || qa.code_snippet || '';
                  const cleanQuestionText = removeCodeBlocksFromQuestion(qa.question_text);
                  
                  // Get cheating detection for this question (key is question index as string)
                  const questionCheating = cheatingDetection?.[String(index)];
                  const hasCheatingFlag = questionCheating && questionCheating.cheatinglikelihood_score >= CHEATING_THRESHOLD;

                  // Get per-question score for this question
                  const questionScore = perQuestionScores?.find(q => q.question_index === index);

                  return (
                    <div key={index} className={`overflow-hidden rounded-xl border bg-white ${hasCheatingFlag ? 'border-red-200' : 'border-light-lila/60'}`}>
                      {/* Question Header - Always Visible and Clickable */}
                      <div 
                        className={`flex justify-between gap-3 px-3 py-3 sm:px-4 sm:py-4 cursor-pointer transition-colors ${hasCheatingFlag ? 'hover:bg-red-50/70' : 'hover:bg-light-lila/10'} ${hasCheatingFlag ? 'items-start md:items-center' : 'items-center'}`}
                        onClick={() => toggleQuestionExpansion(index, setExpandedQuestions)}
                      >
                        <div className="flex flex-col md:flex-row gap-2 flex-1 overflow-hidden">
                          <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                            <div className="flex-shrink-0 mt-0.5">
                              <IconWrapper
                                size="sm"
                                className={`transition-transform duration-200 ${isQuestionExpanded ? 'rotate-90' : ''} ${hasCheatingFlag ? 'text-red' : 'text-dark-invirtus/60'}`}
                              >
                                <ChevronRight />
                              </IconWrapper>
                            </div>
                            <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                              {getQuestionTypeIcon(qa.question_type)}
                              <span className="text-sm font-medium text-dark-invirtus font-body truncate">
                                {qa.question_type === 'introduction'
                                  ? t('meta_cv.qa.introduction')
                                  : (qa.skill_area || questionScore?.skill_area || formatQuestionKind(qa.question_kind))
                                      .replace(/\b\w/g, c => c.toUpperCase())}
                              </span>
                            </div>
                          </div>
                          {/* Suspicious Activity Badge */}
                          {questionCheating && hasCheatingFlag && (
                            <span className="inline-flex shrink-0"> <SuspiciousActivityBadge /></span>
                          )}
                        </div>

                        <div className={`flex md:items-center items-end gap-2 shrink-0 md:ml-2 flex-row`}>
                          {hasCheatingFlag && (
                            <Button
                              type="outlined"
                              intent="secondary"
                              size="xs"
                              icon={<ShieldCheck />}
                              label={t('meta_cv.qa.clear_flag')}
                              className="shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                setConfirmClearIndex(index);
                              }}
                            />
                          )}
                          {(qa.time_spent_seconds != null || qa.time_allocated_minutes != null) && (
                            <Tag
                              intent={getQuestionTimeSpentIntent(qa.time_spent_seconds, qa.time_allocated_minutes)}
                              variant="subtle"
                              title={formatQuestionTimerTitle(qa.time_spent_seconds, qa.time_allocated_minutes)}
                              className="flex items-center justify-center gap-1 sm:!px-3.5 !px-2 min-w-[100px] sm:min-w-[120px]"
                            >
                              <TimerIcon className="sm:w-4 sm:h-4 w-3.5 h-3.5 mb-0.75" />
                              <span className="sm:text-xs text-[11px]">
                                {formatQuestionTimerDisplay(qa.time_spent_seconds, qa.time_allocated_minutes)}
                              </span>
                            </Tag>
                          )}
                        </div>
                      </div>

                      {/* Collapsible Content */}
                      {isQuestionExpanded && (
                        <div className="border-t border-light-lila/60 px-4 pb-4 pt-4 sm:px-5 sm:pb-5 sm:pt-5">
                          <div className="space-y-4">
                            {/* Question Text */}
                            <div>
                              <h4 className="mb-2 text-sm font-medium text-dark-invirtus font-heading">{t('meta_cv.qa.question_label')}</h4>
                              <div className="rounded-lg border border-light-lila/50 bg-light-lila/10 p-3 sm:p-4">
                                <p className="text-sm text-dark-invirtus whitespace-pre-line font-body leading-relaxed">
                                {cleanQuestionText}
                                </p>
                              </div>
                              {codeToShow && (
                               <MonacoIDE
                                isReadOnly
                                initialCode={codeToShow}
                                language={qa.coding_language || "python"}
                                className="w-full min-w-0 mt-4 rounded-md border"
                                interviewId={undefined}
                                questionIndex={index}
                                questionKind={qa.question_kind}
                              />
                            )}
                            </div>

                            {/* Candidate Answer */}
                            {qa.candidate_answer && (
                              <div>
                                <h4 className="mb-2 text-sm font-medium text-dark-invirtus font-heading">{t('meta_cv.qa.audio_response')}</h4>
                                <div className="rounded-lg border border-light-lila/50 bg-gray-50 p-3 sm:p-4">
                                  <p className="text-sm text-gray-700 whitespace-pre-wrap font-body leading-relaxed">
                                  {qa.candidate_answer}
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* Candidate Code */}
                            {qa.candidate_code && (
                              <div>
                                <h4 className="mb-2 text-sm font-medium text-dark-invirtus font-heading">{t('meta_cv.qa.code_solution')}</h4>
                                <MonacoIDE
                                  isReadOnly
                                  initialCode={qa.candidate_code}
                                  language={qa.coding_language || "python"}
                                  className="w-full min-w-0 mt-4 rounded-md border"
                                  interviewId={undefined}
                                  questionIndex={index}
                                  questionKind={qa.question_kind}
                                />
                              </div>
                            )}

                            {/* Empty state — when both transcripts and code are missing.
                                If the classifier produced an inferred score, surface its
                                recruiter-readable explanation; otherwise keep the original
                                neutral wording. */}
                            {!qa.candidate_answer && !qa.candidate_code && (
                              <div className="rounded-lg border border-dashed border-light-lila bg-gray-50 px-4 py-5 text-center">
                                <p className="text-gray-500 text-sm font-body leading-relaxed">
                                  {questionScore?.is_inferred
                                    ? resolveInferredExplanation(questionScore, tInferred)
                                    : t('meta_cv.qa.no_response')}
                                </p>
                              </div>
                            )}

                            {/* Cheating Detection Panel */}
                            {hasCheatingFlag &&
                              questionCheating.suspected_cheating_segments.length > 0 && (
                              <div className="mt-4 rounded-xl border border-light-lila/60 bg-white">
                                {/* Collapsible Header */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCheatingPanel(index);
                                  }}
                                  className="flex w-full flex-wrap items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-light-lila/10"
                                >
                                  <IconWrapper
                                    size="sm"
                                    className={`text-dark-invirtus/50 transition-transform duration-200 ${expandedCheatingPanels[index] ? 'rotate-90' : ''}`}
                                  >
                                    <ChevronRight />
                                  </IconWrapper>
                                  <SuspiciousActivityBadge />
                                  <Tag intent="secondary" variant="subtle">
                                    {t('meta_cv.qa.segment_count', { count: questionCheating.suspected_cheating_segments.length })}
                                  </Tag>
                                </button>
                                
                                {/* Expanded Content */}
                                {expandedCheatingPanels[index] && questionsAnswersData && (
                                  <div className="border-t border-light-lila/60 p-4 sm:p-5">
                                    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(18rem,22rem)]">
                                      {/* Left Column: Video + Overall Analysis */}
                                      <div className="space-y-4 min-w-0">
                                        {/* Video Player with Timeline */}
                                        <QuestionVideoPlayer
                                          ref={(el) => { videoPlayerRefs.current[index] = el; }}
                                          interviewId={questionsAnswersData.interview_id}
                                          questionIndex={index}
                                          segments={questionCheating.suspected_cheating_segments}
                                        />
                                        
                                        {/* Overall Explanation */}
                                        <div className="rounded-lg border border-light-lila/50 bg-gray-50 p-4">
                                          <h5 className="mb-2 text-sm font-medium text-dark-invirtus font-heading">{t('meta_cv.qa.overall_analysis')}</h5>
                                          <p className="text-sm text-gray-700 whitespace-pre-wrap font-body leading-relaxed">
                                            {questionCheating.overall_explanation}
                                          </p>
                                        </div>
                                      </div>
                                      
                                      {/* Right Column: Suspicious Segments */}
                                      <div className="min-w-0 rounded-lg border border-light-lila/50 bg-gray-50 p-4">
                                        <h5 className="mb-3 text-sm font-medium text-dark-invirtus font-heading">{t('meta_cv.qa.suspicious_segments')}</h5>
                                        <div className="space-y-2">
                                          {questionCheating.suspected_cheating_segments.map((segment, segIndex) => {
                                            const segmentKey = `${index}-${segIndex}`;
                                            const isSegmentExpanded = expandedSegments[segmentKey] ?? false;
                                            return (
                                              <div 
                                                key={segIndex} 
                                                onClick={() => handleSegmentClick(index, segment)}
                                                className="group cursor-pointer rounded-lg border border-light-lila/60 bg-white p-3 transition-all hover:border-dark-invirtus/30 hover:shadow-sm"
                                              >
                                                <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
                                                  <div className="flex min-w-0 items-center gap-2">
                                                    <div className={`w-2 h-2 rounded-full ${levelConfig[segment.suspection_level].color}`} />
                                                    <span className="text-sm font-medium text-gray-900 group-hover:text-dark-invirtus transition-colors break-words">
                                                      {formatTime(segment.start_time)} - {formatTime(segment.end_time)}
                                                    </span>
                                                  </div>
                                                  <Tag
                                                    intent={levelConfig[segment.suspection_level].intent}
                                                    variant="subtle"
                                                    className="shrink-0"
                                                  >
                                                    {levelConfig[segment.suspection_level].label}
                                                  </Tag>
                                                </div>
                                                <p 
                                                  className={`text-xs text-gray-600 leading-relaxed font-body ${isSegmentExpanded ? '' : 'line-clamp-2'}`}
                                                >
                                                  {segment.explanation}
                                                </p>
                                                {!isSegmentExpanded && segment.explanation.length > 100 && (
                                                  <Button
                                                    type="text"
                                                    intent="info"
                                                    size="xs"
                                                    onClick={(e) => toggleSegmentExpansion(index, segIndex, e)}
                                                    label={t('meta_cv.qa.show_more')}
                                                    className="mt-1 !px-0"
                                                  />
                                                )}
                                                {isSegmentExpanded && (
                                                  <Button
                                                    type="text"
                                                    intent="info"
                                                    size="xs"
                                                    onClick={(e) => toggleSegmentExpansion(index, segIndex, e)}
                                                    label={t('meta_cv.qa.show_less')}
                                                    className="mt-1 !px-0"
                                                  />
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmationDialog
        isOpen={confirmClearIndex !== null}
        onClose={() => {
          if (clearingIndex === null) {
            setConfirmClearIndex(null);
          }
        }}
        title={t('meta_cv.qa.clear_flag_confirm_title')}
        message={t('meta_cv.qa.clear_flag_confirm_message')}
        closeLabel={t('meta_cv.qa.clear_flag_cancel')}
        buttons={[
          {
            label: t('meta_cv.qa.clear_flag_cancel'),
            type: 'outlined',
            intent: 'secondary',
            onClick: () => setConfirmClearIndex(null),
            disabled: clearingIndex !== null,
          },
          {
            label: t('meta_cv.qa.clear_flag_confirm'),
            intent: 'primary',
            loading: clearingIndex !== null,
            onClick: () => {
              if (confirmClearIndex !== null) {
                handleClearCheatingFlag(confirmClearIndex);
              }
            },
          },
        ]}
      />
    </div>
  );
};